/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import { assign, cloneDeep } from 'lodash';

import { Audit, AuditError, AuditStorage } from '@/pages/audit_setup_wizard/types';
import { DropdownOption } from '@/components/cards/types';

import * as types from './mutationTypes';
import { initAuditState, State } from './state';

const mutations = {
  // UI mutations
  [types.SET_LOADING](state: State, loading: boolean = true) {
    state.isLoading = loading;
  },
  [types.SET_SAVING](state: State, saving: boolean = true) {
    state.isSaving = saving;
  },
  [types.SET_JIRA_ENABLED](state: State, enabled: boolean) {
    state.jiraEnabled = enabled;
  },
  [types.SET_SERVICENOW_ENABLED](state: State, enabled: boolean) {
    state.servicenowEnabled = enabled;
  },
  [types.SET_STORAGE_INTEGRATION_ENABLED](state: State, enabled: boolean) {
    state.storageIntegrationEnabled = enabled;
  },
  [types.SET_REQUEST_MAPPER](state: State, visibility: boolean) {
    state.isRequestMapperVisible = visibility;
  },
  [types.SET_TEMPLATE_ASSESSORS](state: State, assessors: DropdownOption[]) {
    state.templateAssessors = assessors;
  },
  [types.SET_TEMPLATE_VERIFIERS](state: State, verifiers: DropdownOption[]) {
    state.templateVerifiers = verifiers;
  },
  [types.SET_FLAG_NEW_TICKETING](state: State, flag: boolean) {
    state.flagNewTicketing = flag;
  },

  // Audit mutations
  [types.RESET_AUDIT](state: State) {
    state.audit = initAuditState;
  },
  [types.SET_AUDIT](state: State, audit: Audit) {
    state.audit = audit;
  },
  [types.SET_AUDIT_ORIGINAL_STATE](state: State, audit: Audit) {
    state.auditOriginalState = cloneDeep(audit);
  },
  [types.SET_AUDIT_TITLE](state: State, title: string) {
    state.audit.title = title;
  },
  [types.SET_AUDIT_MANAGERS](state: State, managers: DropdownOption[]) {
    state.audit.auditManagers = managers;
  },
  [types.SET_AUDIT_RELATED_PROGRAM](state: State, program: DropdownOption[]) {
    state.audit.relatedProgram = program;
  },
  [types.SET_AUDIT_START_DATE](state: State, date: string) {
    state.audit.startDate = date;
  },
  [types.SET_AUDIT_END_DATE](state: State, date: string) {
    state.audit.endDate = date;
  },
  [types.SET_AUDIT_REPORT_START_DATE](state: State, date: string) {
    state.audit.reportStartDate = date;
  },
  [types.SET_AUDIT_REPORT_END_DATE](state: State, date: string) {
    state.audit.reportEndDate = date;
  },
  [types.SET_AUDIT_TYPE](state: State, type: DropdownOption) {
    state.audit.type = type;
  },
  [types.SET_AUDIT_FIRM](state: State, firm: string) {
    state.audit.auditFirm = firm;
  },
  [types.SET_AUDIT_AUDITORS](state: State, auditors: DropdownOption[]) {
    state.audit.auditors = auditors;
  },
  [types.SET_AUDIT_STORAGE](state: State, storage: DropdownOption) {
    state.audit.managedIn = storage;
  },

  // Audit Error
  [types.SET_AUDIT_ERRORS](state: State, auditError: AuditError) {
    assign(state.auditError, {}, auditError);
  },
  [types.SET_AUDIT_ERROR](state: State, auditError: { field: keyof AuditError; value: string[] }) {
    state.auditError[auditError.field] = auditError.value;
  },
  [types.CLEAR_AUDIT_ERROR](state: State, auditErrorField: keyof AuditError) {
    const fieldErrors = state.auditError[auditErrorField];
    fieldErrors.splice(0, fieldErrors.length);
  },

  // JIRA
  [types.SET_JIRA_DATA](state: State, jiraData: any) {
    state.jiraBus.jiraData = jiraData;
  },
  [types.SET_INITIAL_JIRA_DATA](state: State, jiraData: any) {
    state.jiraBus.initialJiraData = jiraData;
  },
  [types.SET_JIRA_TITLE](state: State, title: any) {
    state.jiraBus.title = title;
  },
  [types.SET_SYNC_EXTERNAL_COMMENTS](state: State, value: any) {
    state.audit.syncExternalComments = value;
  },
  [types.SET_SYNC_EXTERNAL_ATTACHMENTS](state: State, value: any) {
    state.audit.syncExternalAttachments = value;
  },

  // Remote Requests
  [types.SET_REMOTE_REQUESTS](state: State, requests: object[]) {
    state.remoteRequests = requests;
  },

  // Assessment template
  [types.SET_PERFORMING_ASSESSMENT](state: State, value: boolean) {
    state.performingAssessment = value;
  },
  [types.SET_ASSESSORS](state: State, assessors: String) {
    state.audit.assessmentTemplate.assessors = assessors;
  },
  [types.SET_VERIFIERS](state: State, verifiers: String) {
    state.audit.assessmentTemplate.verifiers = verifiers;
  },

  // Storage integration
  [types.SET_STORAGE_INTEGRATION_DATA](state: State, auditStorage: AuditStorage) {
    state.audit.storage = auditStorage;
  },
};

export default mutations;
